/**
 * Shared bracket view components for tournament display.
 * Used by both TournamentResults and BracketViewDebug.
 */

import { Maximize2, ZoomIn, ZoomOut } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { getCountryByCode } from '@/data/countries';
import type { CountryCode } from '@/data/countries/types';
import type { BracketMatch } from '@/lib/tournament/types';
import { cn } from '@/lib/utils';

// Constants for layout calculations
export const MATCH_HEIGHT = 52;
export const MATCH_GAP = 12;
export const ROUND_GAP = 48;
export const MATCH_WIDTH = 180;

// Get round name from round number
export function getRoundName(
  round: number,
  totalRounds: number,
  isThirdPlace?: boolean,
): string {
  if (isThirdPlace) return '3rd Place';
  const fromFinal = totalRounds - round + 1;
  switch (fromFinal) {
    case 1:
      return 'Final';
    case 2:
      return 'Semi-Finals';
    case 3:
      return 'Quarter-Finals';
    case 4:
      return 'Round of 16';
    default:
      return `Round ${round}`;
  }
}

interface MatchCardProps {
  match: BracketMatch;
  compact?: boolean;
  highlightedCountry?: CountryCode | null;
  onCountryClick?: (code: CountryCode) => void;
}

export function MatchCard({
  match,
  compact = false,
  highlightedCountry,
  onCountryClick,
}: MatchCardProps) {
  const countryA = getCountryByCode(match.countryA);
  const countryB = getCountryByCode(match.countryB);

  const isAHighlighted = highlightedCountry === match.countryA;
  const isBHighlighted = highlightedCountry === match.countryB;
  const isMatchHighlighted = isAHighlighted || isBHighlighted;

  return (
    <div
      className={cn(
        'bg-white rounded-lg border shadow-sm overflow-hidden h-full flex flex-col transition-all duration-200',
        compact ? 'text-xs' : 'text-sm',
        isMatchHighlighted
          ? 'ring-2 ring-blue-500 ring-offset-1 border-blue-400'
          : '',
      )}
    >
      {/* Country A */}
      <div
        className={cn(
          'flex-1 flex items-center gap-1.5 px-2 border-b min-h-0 transition-colors cursor-pointer hover:bg-gray-50',
          match.winner === match.countryA
            ? 'bg-green-50'
            : match.winner
              ? 'bg-red-50/30'
              : '',
          isAHighlighted ? '!bg-blue-100 border-blue-300' : '',
        )}
        onClick={(e) => {
          e.stopPropagation();
          onCountryClick?.(match.countryA);
        }}
      >
        <span className="text-sm shrink-0">{countryA?.flag || ''}</span>
        <span
          className={cn(
            'flex-1 truncate font-medium text-xs',
            match.winner === match.countryA
              ? 'text-green-700'
              : match.winner
                ? 'text-gray-400'
                : '',
            isAHighlighted ? '!text-blue-700 font-bold' : '',
          )}
        >
          {countryA?.name || match.countryA}
        </span>
        {match.winner === match.countryA && (
          <span
            className={cn(
              'px-1 py-0.5 text-white text-[9px] font-bold rounded shrink-0',
              isAHighlighted ? 'bg-blue-500' : 'bg-green-500',
            )}
          >
            W
          </span>
        )}
      </div>
      {/* Country B */}
      <div
        className={cn(
          'flex-1 flex items-center gap-1.5 px-2 min-h-0 transition-colors cursor-pointer hover:bg-gray-50',
          match.winner === match.countryB
            ? 'bg-green-50'
            : match.winner
              ? 'bg-red-50/30'
              : '',
          isBHighlighted ? '!bg-blue-100 border-blue-300' : '',
        )}
        onClick={(e) => {
          e.stopPropagation();
          onCountryClick?.(match.countryB);
        }}
      >
        <span className="text-sm shrink-0">{countryB?.flag || ''}</span>
        <span
          className={cn(
            'flex-1 truncate font-medium text-xs',
            match.winner === match.countryB
              ? 'text-green-700'
              : match.winner
                ? 'text-gray-400'
                : '',
            isBHighlighted ? '!text-blue-700 font-bold' : '',
          )}
        >
          {countryB?.name || match.countryB}
        </span>
        {match.winner === match.countryB && (
          <span
            className={cn(
              'px-1 py-0.5 text-white text-[9px] font-bold rounded shrink-0',
              isBHighlighted ? 'bg-blue-500' : 'bg-green-500',
            )}
          >
            W
          </span>
        )}
      </div>
    </div>
  );
}

export interface BracketViewProps {
  bracket: BracketMatch[];
  className?: string;
}

// Pan/Zoom container for infinite canvas experience
interface PanZoomContainerProps {
  children: React.ReactNode;
  className?: string;
  minZoom?: number;
  maxZoom?: number;
  initialZoom?: number;
  onBackgroundClick?: () => void;
}

function PanZoomContainer({
  children,
  className,
  minZoom = 0.25,
  maxZoom = 2,
  initialZoom = 1,
  onBackgroundClick,
}: PanZoomContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const [zoom, setZoom] = useState(initialZoom);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [hasFitToScreen, setHasFitToScreen] = useState(false);

  // Fit content to container on mount
  const fitToScreen = useCallback(() => {
    if (!containerRef.current || !contentRef.current) return;

    const container = containerRef.current.getBoundingClientRect();
    const content = contentRef.current.getBoundingClientRect();

    // Calculate zoom to fit content with some padding
    const padding = 40;
    const scaleX = (container.width - padding * 2) / (content.width / zoom);
    const scaleY = (container.height - padding * 2) / (content.height / zoom);
    const newZoom = Math.min(
      Math.max(Math.min(scaleX, scaleY), minZoom),
      maxZoom,
    );

    // Center the content
    const contentWidth = (content.width / zoom) * newZoom;
    const contentHeight = (content.height / zoom) * newZoom;
    const newX = (container.width - contentWidth) / 2;
    const newY = (container.height - contentHeight) / 2;

    setZoom(newZoom);
    setPosition({ x: newX, y: newY });
  }, [zoom, minZoom, maxZoom]);

  // Auto-fit on mount
  useEffect(() => {
    if (!hasFitToScreen) {
      const timer = setTimeout(() => {
        fitToScreen();
        setHasFitToScreen(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [fitToScreen, hasFitToScreen]);

  // Mouse wheel zoom
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newZoom = Math.min(Math.max(zoom * delta, minZoom), maxZoom);

      // Zoom towards cursor position
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const cursorX = e.clientX - rect.left;
        const cursorY = e.clientY - rect.top;

        const zoomRatio = newZoom / zoom;
        const newX = cursorX - (cursorX - position.x) * zoomRatio;
        const newY = cursorY - (cursorY - position.y) * zoomRatio;

        setPosition({ x: newX, y: newY });
      }

      setZoom(newZoom);
    },
    [zoom, position, minZoom, maxZoom],
  );

  // Mouse drag pan
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return; // Only left click
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    },
    [position],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) return;
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    },
    [isDragging, dragStart],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch support for mobile/tablet
  const [touchStart, setTouchStart] = useState<{
    x: number;
    y: number;
    distance?: number;
  } | null>(null);

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1) {
        setTouchStart({
          x: e.touches[0].clientX - position.x,
          y: e.touches[0].clientY - position.y,
        });
      } else if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        setTouchStart({
          x: (e.touches[0].clientX + e.touches[1].clientX) / 2 - position.x,
          y: (e.touches[0].clientY + e.touches[1].clientY) / 2 - position.y,
          distance,
        });
      }
    },
    [position],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!touchStart) return;

      if (e.touches.length === 1) {
        setPosition({
          x: e.touches[0].clientX - touchStart.x,
          y: e.touches[0].clientY - touchStart.y,
        });
      } else if (e.touches.length === 2 && touchStart.distance) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const scale = distance / touchStart.distance;
        const newZoom = Math.min(Math.max(zoom * scale, minZoom), maxZoom);

        const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2;

        setZoom(newZoom);
        setPosition({
          x: centerX - touchStart.x,
          y: centerY - touchStart.y,
        });
        setTouchStart({ ...touchStart, distance });
      }
    },
    [touchStart, zoom, minZoom, maxZoom],
  );

  const handleTouchEnd = useCallback(() => {
    setTouchStart(null);
  }, []);

  const zoomIn = () => setZoom((z) => Math.min(z * 1.2, maxZoom));
  const zoomOut = () => setZoom((z) => Math.max(z / 1.2, minZoom));

  return (
    <div className={cn('relative overflow-hidden', className)}>
      {/* Zoom controls */}
      <div className="absolute top-2 right-2 z-10 flex flex-col gap-1">
        <Button
          variant="secondary"
          size="icon"
          className="h-8 w-8 shadow-md"
          onClick={zoomIn}
        >
          <ZoomIn className="h-4 w-4" />
        </Button>
        <Button
          variant="secondary"
          size="icon"
          className="h-8 w-8 shadow-md"
          onClick={zoomOut}
        >
          <ZoomOut className="h-4 w-4" />
        </Button>
        <Button
          variant="secondary"
          size="icon"
          className="h-8 w-8 shadow-md"
          onClick={fitToScreen}
        >
          <Maximize2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Zoom level indicator */}
      <div className="absolute bottom-2 right-2 z-10 px-2 py-1 bg-white/80 rounded text-xs font-medium text-gray-600 shadow-sm">
        {Math.round(zoom * 100)}%
      </div>

      {/* Pan/zoom container */}
      <div
        ref={containerRef}
        className={cn(
          'w-full h-full',
          isDragging ? 'cursor-grabbing' : 'cursor-grab',
        )}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={(e) => {
          // Only trigger background click if clicking directly on container
          if (e.target === containerRef.current) {
            onBackgroundClick?.();
          }
        }}
      >
        <div
          ref={contentRef}
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            willChange: 'transform',
            imageRendering: 'auto',
            WebkitFontSmoothing: 'antialiased',
            textRendering: 'optimizeLegibility',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

interface BracketViewContentProps {
  bracket: BracketMatch[];
  highlightedCountry?: CountryCode | null;
  onCountryClick?: (code: CountryCode) => void;
}

// The actual bracket content (used by BracketViewCanvas)
function BracketViewContent({
  bracket,
  highlightedCountry,
  onCountryClick,
}: BracketViewContentProps) {
  const totalRounds = Math.max(...bracket.map((m) => m.round));

  // Group matches by round
  const matchesByRound = useMemo(() => {
    const grouped: Record<number, BracketMatch[]> = {};
    for (let r = 1; r <= totalRounds; r++) {
      grouped[r] = bracket
        .filter((m) => m.round === r && !m.isThirdPlace)
        .sort((a, b) => a.position - b.position);
    }
    return grouped;
  }, [bracket, totalRounds]);

  // Third place match
  const thirdPlaceMatch = bracket.find((m) => m.isThirdPlace);

  // Calculate spacing for vertical alignment
  const getMatchSpacing = (round: number) => {
    if (round === 1) return { gap: MATCH_GAP, marginTop: 0 };
    const multiplier = 2 ** (round - 1);
    const gap = MATCH_GAP + (MATCH_HEIGHT + MATCH_GAP) * (multiplier - 1);
    const marginTop = ((MATCH_HEIGHT + MATCH_GAP) * (multiplier - 1)) / 2;
    return { gap, marginTop };
  };

  const getConnectorHeight = (round: number) => {
    const { gap } = getMatchSpacing(round);
    return gap + MATCH_HEIGHT;
  };

  return (
    <div className="inline-flex p-6" style={{ gap: `${ROUND_GAP}px` }}>
      {Array.from({ length: totalRounds }, (_, i) => i + 1).map((round) => {
        const matches = matchesByRound[round] || [];
        const { gap, marginTop } = getMatchSpacing(round);
        const roundName = getRoundName(round, totalRounds);
        const connectorHeight = getConnectorHeight(round);

        return (
          <div
            key={round}
            className="flex flex-col relative"
            style={{ width: `${MATCH_WIDTH}px` }}
          >
            <div className="text-center mb-4">
              <span
                className={cn(
                  'text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full whitespace-nowrap',
                  round === totalRounds
                    ? 'bg-yellow-100 text-yellow-700 border border-yellow-300'
                    : 'bg-gray-100 text-gray-600',
                )}
              >
                {roundName}
              </span>
            </div>

            <div
              className="flex flex-col"
              style={{ gap: `${gap}px`, marginTop: `${marginTop}px` }}
            >
              {matches.map((match, idx) => {
                const isMatchHighlighted =
                  highlightedCountry &&
                  (match.countryA === highlightedCountry ||
                    match.countryB === highlightedCountry);
                const connectorColor = isMatchHighlighted
                  ? 'bg-blue-400'
                  : 'bg-gray-300';

                return (
                  <div
                    key={match.id}
                    className="relative"
                    style={{ height: `${MATCH_HEIGHT}px` }}
                  >
                    {round > 1 && (
                      <div
                        className={cn(
                          'absolute transition-colors duration-200',
                          connectorColor,
                        )}
                        style={{
                          left: `-${ROUND_GAP / 2}px`,
                          top: '50%',
                          width: `${ROUND_GAP / 2}px`,
                          height: isMatchHighlighted ? '2px' : '1px',
                        }}
                      />
                    )}

                    <MatchCard
                      match={match}
                      compact={totalRounds >= 4}
                      highlightedCountry={highlightedCountry}
                      onCountryClick={onCountryClick}
                    />

                    {round < totalRounds && (
                      <>
                        <div
                          className={cn(
                            'absolute transition-colors duration-200',
                            connectorColor,
                          )}
                          style={{
                            right: `-${ROUND_GAP / 2}px`,
                            top: '50%',
                            width: `${ROUND_GAP / 2}px`,
                            height: isMatchHighlighted ? '2px' : '1px',
                          }}
                        />
                        {idx % 2 === 0 && (
                          <div
                            className={cn(
                              'absolute transition-colors duration-200',
                              connectorColor,
                            )}
                            style={{
                              right: `-${ROUND_GAP / 2}px`,
                              top: '50%',
                              width: isMatchHighlighted ? '2px' : '1px',
                              height: `${connectorHeight / 2}px`,
                            }}
                          />
                        )}
                        {idx % 2 === 1 && (
                          <div
                            className={cn(
                              'absolute transition-colors duration-200',
                              connectorColor,
                            )}
                            style={{
                              right: `-${ROUND_GAP / 2}px`,
                              bottom: '50%',
                              width: isMatchHighlighted ? '2px' : '1px',
                              height: `${connectorHeight / 2}px`,
                            }}
                          />
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Podium - Top 4 finishers */}
      {(() => {
        const finalMatch = bracket.find(
          (m) => m.round === totalRounds && !m.isThirdPlace,
        );
        if (!finalMatch?.winner) return null;

        const first = finalMatch.winner;
        const second =
          finalMatch.countryA === first
            ? finalMatch.countryB
            : finalMatch.countryA;
        const third = thirdPlaceMatch?.winner;
        const fourth = thirdPlaceMatch
          ? thirdPlaceMatch.countryA === third
            ? thirdPlaceMatch.countryB
            : thirdPlaceMatch.countryA
          : undefined;

        const firstCountry = getCountryByCode(first);
        const secondCountry = getCountryByCode(second);
        const thirdCountry = third ? getCountryByCode(third) : null;
        const fourthCountry = fourth ? getCountryByCode(fourth) : null;

        // Podium heights
        const heights = { first: 100, second: 75, third: 58, fourth: 42 };

        // Check if each position is highlighted
        const isFirstHighlighted = highlightedCountry === first;
        const isSecondHighlighted = highlightedCountry === second;
        const isThirdHighlighted = third && highlightedCountry === third;
        const isFourthHighlighted = fourth && highlightedCountry === fourth;

        return (
          <div
            className="flex flex-col items-center"
            style={{ marginTop: `${getMatchSpacing(totalRounds).marginTop}px` }}
          >
            {/* Podium - classic 2-1-3-4 layout */}
            <div className="flex items-end justify-center gap-1.5">
              {/* 2nd Place */}
              <div
                className={cn(
                  'flex flex-col items-center cursor-pointer transition-all duration-200',
                  isSecondHighlighted && 'scale-105',
                )}
                onClick={() => onCountryClick?.(second)}
              >
                {secondCountry && (
                  <div className="flex flex-col items-center mb-1.5">
                    <span className="text-2xl mb-0.5">
                      {secondCountry.flag}
                    </span>
                    <span
                      className={cn(
                        'text-[10px] font-medium text-center max-w-[70px] truncate',
                        isSecondHighlighted && 'text-blue-600 font-bold',
                      )}
                    >
                      {secondCountry.name}
                    </span>
                  </div>
                )}
                <div
                  className={cn(
                    'w-20 bg-gradient-to-b from-gray-300 to-gray-400 rounded-t-lg flex items-start justify-center pt-2 border-2 border-gray-400 border-b-0 transition-all duration-200',
                    isSecondHighlighted && 'ring-2 ring-blue-500 ring-offset-1',
                  )}
                  style={{ height: `${heights.second}px` }}
                >
                  <span className="text-xl">🥈</span>
                </div>
              </div>

              {/* 1st Place */}
              <div
                className={cn(
                  'flex flex-col items-center cursor-pointer transition-all duration-200',
                  isFirstHighlighted && 'scale-105',
                )}
                onClick={() => onCountryClick?.(first)}
              >
                {firstCountry && (
                  <div className="flex flex-col items-center mb-1.5">
                    <span className="text-3xl mb-0.5">{firstCountry.flag}</span>
                    <span
                      className={cn(
                        'text-xs font-bold text-center max-w-[80px] truncate',
                        isFirstHighlighted && 'text-blue-600',
                      )}
                    >
                      {firstCountry.name}
                    </span>
                  </div>
                )}
                <div
                  className={cn(
                    'w-24 bg-gradient-to-b from-yellow-300 to-yellow-500 rounded-t-lg flex items-start justify-center pt-2 border-2 border-yellow-500 border-b-0 transition-all duration-200',
                    isFirstHighlighted && 'ring-2 ring-blue-500 ring-offset-1',
                  )}
                  style={{ height: `${heights.first}px` }}
                >
                  <span className="text-2xl">🏆</span>
                </div>
              </div>

              {/* 3rd Place */}
              {third && (
                <div
                  className={cn(
                    'flex flex-col items-center cursor-pointer transition-all duration-200',
                    isThirdHighlighted && 'scale-105',
                  )}
                  onClick={() => onCountryClick?.(third)}
                >
                  {thirdCountry && (
                    <div className="flex flex-col items-center mb-1.5">
                      <span className="text-2xl mb-0.5">
                        {thirdCountry.flag}
                      </span>
                      <span
                        className={cn(
                          'text-[10px] font-medium text-center max-w-[70px] truncate',
                          isThirdHighlighted && 'text-blue-600 font-bold',
                        )}
                      >
                        {thirdCountry.name}
                      </span>
                    </div>
                  )}
                  <div
                    className={cn(
                      'w-20 bg-gradient-to-b from-amber-500 to-amber-700 rounded-t-lg flex items-start justify-center pt-2 border-2 border-amber-600 border-b-0 transition-all duration-200',
                      isThirdHighlighted &&
                        'ring-2 ring-blue-500 ring-offset-1',
                    )}
                    style={{ height: `${heights.third}px` }}
                  >
                    <span className="text-xl">🥉</span>
                  </div>
                </div>
              )}

              {/* 4th Place */}
              {fourth && fourthCountry && (
                <div
                  className={cn(
                    'flex flex-col items-center ml-1 cursor-pointer transition-all duration-200',
                    isFourthHighlighted && 'scale-105',
                  )}
                  onClick={() => onCountryClick?.(fourth)}
                >
                  <div className="flex flex-col items-center mb-1.5">
                    <span className="text-xl mb-0.5">{fourthCountry.flag}</span>
                    <span
                      className={cn(
                        'text-[9px] font-medium text-center max-w-[60px] truncate text-muted-foreground',
                        isFourthHighlighted && '!text-blue-600 font-bold',
                      )}
                    >
                      {fourthCountry.name}
                    </span>
                  </div>
                  <div
                    className={cn(
                      'w-16 bg-gradient-to-b from-stone-300 to-stone-400 rounded-t-lg flex items-start justify-center pt-2 border-2 border-stone-400 border-b-0 transition-all duration-200',
                      isFourthHighlighted &&
                        'ring-2 ring-blue-500 ring-offset-1',
                    )}
                    style={{ height: `${heights.fourth}px` }}
                  >
                    <span className="text-lg font-bold text-stone-600">4</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// Canvas-based bracket view with pan/zoom for tablet and desktop
export function BracketViewCanvas({ bracket, className }: BracketViewProps) {
  const [highlightedCountry, setHighlightedCountry] =
    useState<CountryCode | null>(null);

  const handleCountryClick = useCallback((code: CountryCode) => {
    setHighlightedCountry((prev) => (prev === code ? null : code));
  }, []);

  const handleBackgroundClick = useCallback(() => {
    setHighlightedCountry(null);
  }, []);

  // Get highlighted country info
  const highlightedCountryInfo = highlightedCountry
    ? getCountryByCode(highlightedCountry)
    : null;

  // Filter out empty placeholder matches
  const validBracket = bracket.filter((m) => m.countryA && m.countryB);

  if (validBracket.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center text-muted-foreground',
          className,
        )}
      >
        No bracket data available
      </div>
    );
  }

  return (
    <div className={cn('relative', className)}>
      {/* Highlighted country info banner */}
      {highlightedCountryInfo && (
        <div className="absolute top-2 left-2 z-10 flex items-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-lg shadow-lg">
          <span className="text-lg">{highlightedCountryInfo.flag}</span>
          <span className="font-semibold text-sm">
            {highlightedCountryInfo.name}
          </span>
          <button
            onClick={() => setHighlightedCountry(null)}
            className="ml-2 p-0.5 hover:bg-blue-600 rounded"
          >
            <span className="text-xs">✕</span>
          </button>
        </div>
      )}

      <PanZoomContainer
        className="bg-stone-50 h-full"
        onBackgroundClick={handleBackgroundClick}
      >
        <BracketViewContent
          bracket={validBracket}
          highlightedCountry={highlightedCountry}
          onCountryClick={handleCountryClick}
        />
      </PanZoomContainer>
    </div>
  );
}

// Mobile-optimized vertical bracket view
export function BracketViewMobile({ bracket, className }: BracketViewProps) {
  // Filter out empty placeholder matches
  const validBracket = bracket.filter((m) => m.countryA && m.countryB);
  const totalRounds = Math.max(...validBracket.map((m) => m.round), 0);

  // Group matches by round (reverse order for mobile - show finals first)
  const rounds = useMemo(() => {
    const result: { round: number; name: string; matches: BracketMatch[] }[] =
      [];

    for (let r = totalRounds; r >= 1; r--) {
      const matches = validBracket
        .filter((m) => m.round === r && !m.isThirdPlace)
        .sort((a, b) => a.position - b.position);
      result.push({
        round: r,
        name: getRoundName(r, totalRounds),
        matches,
      });
    }

    return result;
  }, [validBracket, totalRounds]);

  // Third place match
  const thirdPlaceMatch = validBracket.find((m) => m.isThirdPlace);

  // Get top 4 for podium
  const finalMatch = validBracket.find(
    (m) => m.round === totalRounds && !m.isThirdPlace,
  );
  const podiumData = useMemo(() => {
    if (!finalMatch?.winner) return null;
    const first = finalMatch.winner;
    const second =
      finalMatch.countryA === first ? finalMatch.countryB : finalMatch.countryA;
    const third = thirdPlaceMatch?.winner;
    const fourth = thirdPlaceMatch
      ? thirdPlaceMatch.countryA === third
        ? thirdPlaceMatch.countryB
        : thirdPlaceMatch.countryA
      : undefined;
    return {
      first: getCountryByCode(first),
      second: getCountryByCode(second),
      third: third ? getCountryByCode(third) : null,
      fourth: fourth ? getCountryByCode(fourth) : null,
    };
  }, [finalMatch, thirdPlaceMatch]);

  if (validBracket.length === 0) {
    return (
      <div
        className={cn(
          'flex items-center justify-center text-muted-foreground',
          className,
        )}
      >
        No bracket data available
      </div>
    );
  }

  return (
    <div className={cn('overflow-y-auto', className)}>
      <div className="p-3 space-y-4">
        {/* Compact Podium */}
        {podiumData?.first && (
          <div className="flex items-end justify-center gap-1 py-2">
            {/* 2nd */}
            <div className="flex flex-col items-center">
              <span className="text-lg">{podiumData.second?.flag}</span>
              <span className="text-[9px] font-medium truncate max-w-[50px]">
                {podiumData.second?.name}
              </span>
              <div className="w-12 h-10 bg-gradient-to-b from-gray-300 to-gray-400 rounded-t flex items-center justify-center mt-1">
                <span className="text-sm">🥈</span>
              </div>
            </div>
            {/* 1st */}
            <div className="flex flex-col items-center">
              <span className="text-2xl">{podiumData.first.flag}</span>
              <span className="text-[10px] font-bold truncate max-w-[60px]">
                {podiumData.first.name}
              </span>
              <div className="w-14 h-14 bg-gradient-to-b from-yellow-300 to-yellow-500 rounded-t flex items-center justify-center mt-1">
                <span className="text-lg">🏆</span>
              </div>
            </div>
            {/* 3rd */}
            {podiumData.third && (
              <div className="flex flex-col items-center">
                <span className="text-lg">{podiumData.third.flag}</span>
                <span className="text-[9px] font-medium truncate max-w-[50px]">
                  {podiumData.third.name}
                </span>
                <div className="w-12 h-8 bg-gradient-to-b from-amber-500 to-amber-700 rounded-t flex items-center justify-center mt-1">
                  <span className="text-sm">🥉</span>
                </div>
              </div>
            )}
            {/* 4th */}
            {podiumData.fourth && (
              <div className="flex flex-col items-center">
                <span className="text-base">{podiumData.fourth.flag}</span>
                <span className="text-[8px] text-muted-foreground truncate max-w-[45px]">
                  {podiumData.fourth.name}
                </span>
                <div className="w-10 h-6 bg-gradient-to-b from-stone-300 to-stone-400 rounded-t flex items-center justify-center mt-1">
                  <span className="text-xs font-bold text-stone-600">4</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Rounds */}
        {rounds.map(({ round, name, matches }) => (
          <div key={round}>
            {/* Round header */}
            <div className="flex items-center gap-2 mb-2">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-2 py-0.5 bg-muted/50 rounded-full">
                {name}
              </span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>

            {/* Matches grid */}
            <div
              className={cn(
                'grid gap-2',
                matches.length === 1
                  ? 'grid-cols-1 max-w-[200px] mx-auto'
                  : matches.length === 2
                    ? 'grid-cols-2'
                    : 'grid-cols-2',
              )}
            >
              {matches.map((match) => (
                <div key={match.id} style={{ height: `${MATCH_HEIGHT - 4}px` }}>
                  <MatchCard match={match} compact />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
